import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, CATEGORIES } from "@/lib/api";
import { PageHeader, Panel, Field, NSelect } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const EMPTY = { client_name: "", client_address: "", client_contact: "", object: "", service_id: "", tva_rate: 16, validity_days: 30, notes: "", lines: [{ description: "", quantity: 1, unit_price: 0 }] };

export const ServiceOptions = ({ services }) =>
  Object.entries(CATEGORIES).map(([k, c]) => (
    <optgroup key={k} label={c.label}>
      {services.filter((s) => s.category === k).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </optgroup>
  ));

export default function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reqId = params.get("demande");
  const [f, setF] = useState(EMPTY);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.active)));
    if (id) api.get(`/quotes/${id}`).then((r) => setF({ ...EMPTY, ...r.data, service_id: r.data.service_id || "" }));
    else api.get("/settings/company").then((r) => setF((p) => ({ ...p, tva_rate: r.data.tva_default })));
    if (!id && reqId) api.get(`/requests/${reqId}`).then(({ data: r }) => setF((p) => ({
      ...p, request_id: r.id, client_name: r.company || r.name, client_address: r.location,
      client_contact: [r.company && r.name, r.phone, r.email].filter(Boolean).join(" · "),
      object: r.service_name !== "—" ? r.service_name : "", service_id: r.service_id || "", notes: `Réf. demande ${r.number}`,
    })));
  }, [id, reqId]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const pickService = (sid) => {
    set("service_id", sid);
    const s = services.find((x) => x.id === sid);
    if (s && f.lines.length === 1 && !f.lines[0].description) {
      setLine(0, "description", s.name);
      if (s.base_price) setLine(0, "unit_price", s.base_price);
    }
  };

  const sub = f.lines.reduce((a, l) => a + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const tva = (sub * Number(f.tva_rate || 0)) / 100;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...f, service_id: f.service_id || null, tva_rate: Number(f.tva_rate), validity_days: Number(f.validity_days),
      lines: f.lines.map((l) => ({ description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })) };
    try {
      const r = id ? await api.put(`/quotes/${id}`, body) : await api.post("/quotes", body);
      toast.success(id ? "Devis mis à jour" : `Devis ${r.data.number} créé`);
      navigate(`/devis/${r.data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="quote-form">
      <PageHeader kicker="Gestion commerciale" title={id ? `Modifier ${f.number || ""}` : "Nouveau devis interne"}>
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="quote-cancel-btn">Annuler</Button>
        <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="quote-save-btn"><Save className="mr-1 h-4 w-4" /> Enregistrer</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Panel title="Client & objet">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Client *"><Input required value={f.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="quote-client-name-input" /></Field>
              <Field label="Contact"><Input value={f.client_contact} onChange={(e) => set("client_contact", e.target.value)} data-testid="quote-client-contact-input" /></Field>
              <Field label="Adresse" className="sm:col-span-2"><Input value={f.client_address} onChange={(e) => set("client_address", e.target.value)} data-testid="quote-client-address-input" /></Field>
              <Field label="Objet *" className="sm:col-span-2"><Input required value={f.object} onChange={(e) => set("object", e.target.value)} data-testid="quote-object-input" /></Field>
              <Field label="Service concerné" className="sm:col-span-2">
                <NSelect value={f.service_id} onChange={pickService} testId="quote-service-select">
                  <option value="">— Choisir un service —</option>
                  <ServiceOptions services={services} />
                </NSelect>
              </Field>
            </div>
          </Panel>
          <Panel title="Lignes de prestation" action={
            <Button type="button" size="sm" variant="outline" data-testid="quote-add-line-btn" onClick={() => set("lines", [...f.lines, { description: "", quantity: 1, unit_price: 0 }])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Ligne
            </Button>}>
            <div className="space-y-3">
              {f.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`quote-line-${i}`}>
                  <Field label="Désignation" className="col-span-12 sm:col-span-6"><Input required value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`quote-line-desc-${i}`} /></Field>
                  <Field label="Qté" className="col-span-3 sm:col-span-2"><Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`quote-line-qty-${i}`} /></Field>
                  <Field label="P.U. (USD)" className="col-span-5 sm:col-span-2"><Input type="number" step="any" min="0" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`quote-line-price-${i}`} /></Field>
                  <div className="col-span-4 flex h-9 items-center justify-end gap-2 sm:col-span-2">
                    <span className="text-xs font-semibold">{money(l.quantity * l.unit_price)}</span>
                    {f.lines.length > 1 && (
                      <button type="button" onClick={() => set("lines", f.lines.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`quote-line-remove-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Conditions & remarques">
            <Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="quote-notes-input" />
          </Panel>
        </div>
        <aside className="h-fit space-y-4 rounded-md border border-slate-200 bg-white p-5 lg:sticky lg:top-24">
          <div className="grid grid-cols-2 gap-3">
            <Field label="TVA %"><Input type="number" step="any" value={f.tva_rate} onChange={(e) => set("tva_rate", e.target.value)} data-testid="quote-tva-input" /></Field>
            <Field label="Validité (j)"><Input type="number" value={f.validity_days} onChange={(e) => set("validity_days", e.target.value)} data-testid="quote-validity-input" /></Field>
          </div>
          <dl className="space-y-2 border-t border-slate-100 pt-4 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Total HT</dt><dd data-testid="quote-subtotal">{money(sub)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">TVA</dt><dd>{money(tva)}</dd></div>
            <div className="flex justify-between border-t-2 border-red-600 pt-2 font-display text-lg font-bold"><dt>Total TTC</dt><dd data-testid="quote-total">{money(sub + tva)}</dd></div>
          </dl>
        </aside>
      </div>
    </form>
  );
}
